import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-lg text-primary-100">
            Last updated: January 4, 2026
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 mb-6">
              By accessing or using StockWise Inventory ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-6">
              StockWise Inventory is a cloud-based inventory management system that allows businesses to track products, manage warehouses, generate reports, and streamline operations. We provide the Service on a subscription basis with different pricing tiers.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Account Registration</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Account Creation</h3>
            <p className="text-gray-700 mb-6">
              To use the Service, you must create an account by providing accurate and complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Account Responsibilities</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>You must be at least 18 years old to create an account</li>
              <li>You must provide accurate, current, and complete information</li>
              <li>You must maintain the security of your account</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>You are responsible for all activities under your account</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription and Payment</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Free Trial</h3>
            <p className="text-gray-700 mb-6">
              We offer a 7-day free trial for new users. No credit card is required for the trial. At the end of the trial period, you must choose a paid plan to continue using the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Subscription Plans</h3>
            <p className="text-gray-700 mb-6">
              We offer multiple subscription plans with different features and limits. Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Payment Terms</h3>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Payments are processed through secure third-party payment processors</li>
              <li>You authorize us to charge your payment method for all fees</li>
              <li>Prices are subject to change with 30 days notice</li>
              <li>Failed payments may result in service suspension</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">4.4 Cancellation and Refunds</h3>
            <p className="text-gray-700 mb-6">
              You may cancel your subscription at any time. Cancellations take effect at the end of the current billing period. We do not provide refunds for partial months or unused portions of annual subscriptions, except as required by law.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Permitted Use</h3>
            <p className="text-gray-700 mb-6">
              You may use the Service only for lawful purposes and in accordance with these Terms. You agree to use the Service for managing your business inventory and related operations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Prohibited Activities</h3>
            <p className="text-gray-700 mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Upload malicious code or viruses</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Use the Service to send spam or unsolicited messages</li>
              <li>Reverse engineer or decompile the Service</li>
              <li>Resell or redistribute the Service without permission</li>
              <li>Use the Service for illegal activities</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Data</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Data Ownership</h3>
            <p className="text-gray-700 mb-6">
              You retain all rights to your business data stored in the Service. We do not claim ownership of your data. You grant us a limited license to use your data solely to provide the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Data Backup</h3>
            <p className="text-gray-700 mb-6">
              We perform regular automated backups of your data. However, you are responsible for maintaining your own backup copies of important data. We are not liable for any data loss.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Data Export</h3>
            <p className="text-gray-700 mb-6">
              You may export your data at any time using our export features. Upon account termination, you have 30 days to export your data before it is permanently deleted.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-700 mb-6">
              The Service, including all content, features, and functionality, is owned by StockWise Inventory and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Service Availability</h2>
            <p className="text-gray-700 mb-6">
              We strive to provide 99.9% uptime but do not guarantee uninterrupted access to the Service. We may perform scheduled maintenance with advance notice. We are not liable for any downtime or service interruptions.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-6">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, STOCKWISE INVENTORY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
            <p className="text-gray-700 mb-6">
              OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Warranty Disclaimer</h2>
            <p className="text-gray-700 mb-6">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700 mb-6">
              You agree to indemnify and hold harmless StockWise Inventory from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the Service or violation of these Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
            <p className="text-gray-700 mb-6">
              We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease. All provisions that should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by email or through the Service. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="text-gray-700 mb-6">
              These Terms shall be governed by and construed in accordance with the laws of Ethiopia, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Addis Ababa, Ethiopia.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <ul className="list-none mb-6 text-gray-700 space-y-2">
              <li><strong>Email:</strong> support@stockwise-inventory.cloud</li>
              <li><strong>Telegram:</strong> @stockwiseinventory96</li>
              <li><strong>Website:</strong> https://stockwise-inventory.cloud</li>
            </ul>

            <div className="mt-12 p-6 bg-primary-50 rounded-lg border border-primary-200">
              <p className="text-sm text-primary-900">
                <strong>Note:</strong> These terms of service are provided as a template and should be reviewed by a legal professional to ensure compliance with applicable laws in your jurisdiction.
              </p>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfService;
